import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Loader2, Palette, Globe, Users, CreditCard, HardDrive, Wallet, Settings } from 'lucide-react';
import { toast } from 'sonner';

// Already-extracted sub-tabs
import { AdminPayoutsTab } from './AdminPayoutsTab';
import { PayMongoTab } from './admin-settings/PayMongoTab';
import { EmailTab } from './admin-settings/EmailTab';
import { MaintenanceTab, TestUsersCleanupCard } from './admin-settings/MaintenanceTab';
import { AdminActivatedBackfillCard } from './admin-settings/AdminActivatedBackfillCard';
import { OnboardingFollowupsCard } from './admin-settings/OnboardingFollowupsCard';
import { CategoryBackfillCard } from './CategoryBackfillCard';
import { MerchantDigestCard } from './MerchantDigestCard';
import { AuditRetentionCard } from './AuditRetentionCard';

// New: refactored tab components
import { SiteIdentityTab } from './admin-settings/SiteIdentityTab';
import { ThemeTab } from './admin-settings/ThemeTab';
import { ReferralTab } from './admin-settings/ReferralTab';
import { StorageTab } from './admin-settings/StorageTab';

/**
 * AdminSettingsPage — orchestrator for platform-wide configuration.
 *
 * Each tab body lives in its own file under ./admin-settings/. This page
 * still owns all config state + save handlers so the tabs stay thin and
 * easy to test in isolation.
 */
export function AdminSettingsPage() {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('site');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ─── Config state slices ────────────────────────────────────────────────
  const [siteIdentity, setSiteIdentity] = useState({
    site_name: '', tagline: '', description: '',
    logo_url: '', icon_url: '', favicon_url: '', og_image_url: '',
    contact_email: '', contact_phone: '', address: '',
  });

  const [theme, setTheme] = useState({
    primary_color: '#00E5FF', secondary_color: '#007AFF', accent_color: '#00E676',
    background_color: '#000B18', card_color: '#041A3B', text_color: '#FFFFFF',
    muted_color: '#A0B4CC', danger_color: '#FF1744', warning_color: '#FFEA00',
    font_heading: 'Outfit', font_body: 'Manrope',
  });

  const [referral, setReferral] = useState({
    level_1_rate: 0.10, level_2_rate: 0.05, level_3_rate: 0,
    min_withdrawal: 100, max_levels: 2, is_enabled: true,
  });
  const [mroRate, setMroRate] = useState(5);

  const [paymongo, setPaymongo] = useState({
    secret_key: '', public_key: '', webhook_secret: '',
    is_live: false, is_enabled: false,
    card_mdr: 3.5, card_fixed_fee: 10,
    gcash_mdr: 2,   gcash_fixed_fee: 5,
    maya_mdr: 2.5,  maya_fixed_fee: 5,
  });

  const [smtp, setSmtp] = useState({
    provider: 'smtp', host: '', port: 587, username: '', password: '',
    from_email: 'noreply@allglobalaccess.com', from_name: 'All Global Access', reply_to: '',
    api_key: '', is_enabled: false,
  });

  const [storage, setStorage] = useState({
    mode: 'local', is_enabled: true,
    r2_access_key_id: '', r2_secret_access_key: '', r2_account_id: '', r2_bucket: '', r2_public_url: '',
    emergent_key: '', emergent_app_name: 'all-global-access',
    active_mode: 'local', r2_secret_access_key_set: false, emergent_key_set: false,
  });

  // ─── Initial load ───────────────────────────────────────────────────────
  const fetchConfig = useCallback(async () => {
    try {
      const [configRes, paymongoRes, smtpRes, storageRes, mroRes] = await Promise.all([
        api.get('/api/config/all'),
        api.get('/api/config/paymongo').catch(() => ({ data: null })),
        api.get('/api/config/smtp').catch(() => ({ data: null })),
        api.get('/api/config/storage').catch(() => ({ data: null })),
        api.get('/api/admin/mro/commission-settings').catch(() => ({ data: null })),
      ]);
      const data = configRes.data;
      const strip = (obj) => {
        if (!obj) return {};
        // eslint-disable-next-line no-unused-vars
        const { type, _id, updated_at, updated_by, ...rest } = obj;
        Object.keys(rest).forEach(k => { if (rest[k] === null) rest[k] = ''; });
        return rest;
      };
      if (data.site_identity) setSiteIdentity(prev => ({ ...prev, ...strip(data.site_identity) }));
      if (data.theme) setTheme(prev => ({ ...prev, ...strip(data.theme) }));
      if (data.referral) setReferral(prev => ({ ...prev, ...strip(data.referral) }));
      if (paymongoRes.data) setPaymongo(prev => ({ ...prev, ...strip(paymongoRes.data) }));
      if (smtpRes.data) setSmtp(prev => ({ ...prev, ...strip(smtpRes.data) }));
      if (storageRes.data) setStorage(prev => ({ ...prev, ...strip(storageRes.data) }));
      if (mroRes.data) {
        const v = (typeof mroRes.data.merchant_bonus === 'number') ? mroRes.data.merchant_bonus : mroRes.data.rate;
        if (typeof v === 'number') setMroRate(v);
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // ─── Save handlers (one per config slice) ───────────────────────────────
  const saveWith = (url, body, successMsg, errMsg) => async () => {
    setSaving(true);
    try {
      await api.put(url, body);
      toast.success(successMsg);
    } catch (error) {
      toast.error(error.response?.data?.detail || errMsg);
    } finally {
      setSaving(false);
    }
  };

  const saveSiteIdentity = saveWith('/api/config/site-identity', siteIdentity, 'Site identity saved!', 'Failed to save site identity');
  const saveTheme        = saveWith('/api/config/theme',        theme,        'Theme settings saved!','Failed to save theme');
  const saveReferral     = saveWith('/api/config/referral',     referral,     'Referral settings saved!','Failed to save referral config');
  const savePaymongo     = saveWith('/api/config/paymongo',     paymongo,     'PayMongo configuration saved!','Failed to save PayMongo config');
  const saveSmtp         = saveWith('/api/config/smtp',         smtp,         'Email configuration saved!','Failed to save email config');

  const saveMroRate = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/mro/commission-settings', { merchant_bonus: parseFloat(mroRate) });
      toast.success('MRO merchant onboarding bonus updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update MRO bonus');
    } finally {
      setSaving(false);
    }
  };

  const testSmtp = async () => {
    try {
      const { data } = await api.post('/api/config/smtp/test');
      toast.success(data.message || 'Test email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP test failed');
    }
  };

  // Storage save is special — server may fall back from R2/Emergent → Local
  // and we need to surface the reason loudly.
  const saveStorage = async () => {
    setSaving(true);
    try {
      const payload = { ...storage };
      delete payload.active_mode;
      delete payload.active_mode_reason;
      delete payload.r2_secret_access_key_set;
      delete payload.emergent_key_set;
      const { data } = await api.put('/api/config/storage', payload);
      const expected = (payload.mode || 'local').toLowerCase();
      const got = (data.active_mode || 'local').toLowerCase();
      if (expected !== 'local' && got === 'local') {
        toast.error(`Saved, but storage stayed LOCAL: ${data.error || data.warning || data.reason || 'unknown reason'}`, { duration: 10000 });
      } else {
        toast.success(`Storage saved — active mode: ${(data.active_mode || '').toUpperCase()}`);
      }
      const fresh = await api.get('/api/config/storage');
      setStorage(prev => ({ ...prev, ...fresh.data, r2_secret_access_key: '', emergent_key: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save storage config');
    } finally {
      setSaving(false);
    }
  };

  const testStorage = async () => {
    try {
      const { data } = await api.post('/api/config/storage/test');
      if (data.success) toast.success(data.message);
      else toast.error(data.message || 'Storage test failed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Storage test failed');
    }
  };

  // ─── Local→R2 / Local→Emergent migration ────────────────────────────────
  const [migration, setMigration] = useState({ state: 'idle', totals: {}, errors: [] });
  const pollMigration = useCallback(async () => {
    try {
      const { data } = await api.get('/api/config/storage/migration/status');
      setMigration(data || { state: 'idle', totals: {}, errors: [] });
      return data?.state;
    } catch {
      return null;
    }
  }, [api]);

  useEffect(() => {
    pollMigration();
    const id = setInterval(() => {
      if (migration.state === 'running') pollMigration();
    }, 3000);
    return () => clearInterval(id);
  }, [pollMigration, migration.state]);

  const startMigration = async () => {
    try {
      const { data } = await api.post('/api/config/storage/migration/start');
      toast.success(data.message || 'Migration started');
      setMigration({ state: 'running', totals: {}, errors: [] });
      const tick = async () => {
        const s = await pollMigration();
        if (s === 'running') setTimeout(tick, 2500);
      };
      tick();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start migration');
    }
  };

  // ─── Image upload (used by SiteIdentityTab logo / favicon / og fields) ──
  const uploadImage = async (file, field) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/api/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = data.url || `/api/files/${data.id}`;
      setSiteIdentity(prev => ({ ...prev, [field]: url }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    }
  };

  if (loading) {
    return (
      <AdminSidebarLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </AdminSidebarLayout>
    );
  }

  const tabTriggerClass = "data-[state=active]:bg-[#00E5FF] data-[state=active]:text-[#000B18]";

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="mb-8">
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">Platform Settings</h1>
          <p className="text-[#A0B4CC] mt-1">Configure your platform&apos;s appearance and behavior</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#041A3B] border border-white/10 mb-6">
            <TabsTrigger value="site"        className={tabTriggerClass}><Globe className="w-4 h-4 mr-2" />Site</TabsTrigger>
            <TabsTrigger value="theme"       className={tabTriggerClass}><Palette className="w-4 h-4 mr-2" />Theme</TabsTrigger>
            <TabsTrigger value="referral"    className={tabTriggerClass}><Users className="w-4 h-4 mr-2" />Referral</TabsTrigger>
            <TabsTrigger value="paymongo"    className={tabTriggerClass}><CreditCard className="w-4 h-4 mr-2" />PayMongo</TabsTrigger>
            <TabsTrigger value="email"       className={tabTriggerClass}><Globe className="w-4 h-4 mr-2" />Email</TabsTrigger>
            <TabsTrigger value="payouts"     className={tabTriggerClass} data-testid="payouts-tab"><Wallet className="w-4 h-4 mr-2" />Payouts</TabsTrigger>
            <TabsTrigger value="storage"     className={tabTriggerClass} data-testid="storage-tab"><HardDrive className="w-4 h-4 mr-2" />Storage</TabsTrigger>
            <TabsTrigger value="maintenance" className="data-[state=active]:bg-amber-500 data-[state=active]:text-[#000B18]" data-testid="maintenance-tab-trigger"><Settings className="w-4 h-4 mr-2" />Maintenance</TabsTrigger>
          </TabsList>

          <TabsContent value="site">
            <SiteIdentityTab
              siteIdentity={siteIdentity} setSiteIdentity={setSiteIdentity}
              saveSiteIdentity={saveSiteIdentity} uploadImage={uploadImage} saving={saving}
            />
          </TabsContent>

          <TabsContent value="theme">
            <ThemeTab theme={theme} setTheme={setTheme} saveTheme={saveTheme} saving={saving} />
          </TabsContent>

          <TabsContent value="referral">
            <ReferralTab
              referral={referral} setReferral={setReferral} saveReferral={saveReferral}
              mroRate={mroRate} setMroRate={setMroRate} saveMroRate={saveMroRate}
              saving={saving}
            />
          </TabsContent>

          <TabsContent value="paymongo">
            <PayMongoTab paymongo={paymongo} setPaymongo={setPaymongo} savePaymongo={savePaymongo} saving={saving} />
          </TabsContent>

          <TabsContent value="email">
            <EmailTab smtp={smtp} setSmtp={setSmtp} saveSmtp={saveSmtp} testSmtp={testSmtp} saving={saving} />
          </TabsContent>

          <TabsContent value="payouts">
            <AdminPayoutsTab />
          </TabsContent>

          <TabsContent value="storage">
            <StorageTab
              api={api}
              storage={storage} setStorage={setStorage}
              saveStorage={saveStorage} testStorage={testStorage}
              migration={migration} startMigration={startMigration}
              saving={saving}
            />
          </TabsContent>

          <TabsContent value="maintenance">
            <MaintenanceTab />
            <TestUsersCleanupCard />
            <CategoryBackfillCard api={api} />
            <MerchantDigestCard api={api} />
            <AuditRetentionCard api={api} />
            <AdminActivatedBackfillCard />
            <OnboardingFollowupsCard />
          </TabsContent>
        </Tabs>
      </div>
    </AdminSidebarLayout>
  );
}
